/** @fileoverview Caps how many app data calls run at the same time, and queues the rest. */

/*
 * The gateway turns away a page that has more than about 10 calls open at once, so a screen
 * with many tables lost most of them on first load. Six leaves room for the page's other calls.
 */
const MAX_RUNNING_CALLS = 6;

type WaitingCall = { start: () => void; signal?: AbortSignal; onAbort: () => void };

let runningCalls = 0;
const waitingCalls: WaitingCall[] = [];

/** Starts the next waiting calls while there is a free place. */
const startWaitingCalls = () => {
  while (runningCalls < MAX_RUNNING_CALLS && waitingCalls.length) {
    const next = waitingCalls.shift()!;
    next.signal?.removeEventListener('abort', next.onAbort);
    next.start();
  }
};

/**
 * Runs a call once fewer than the maximum are running; until then it waits its turn.
 * A call whose signal aborts while it waits leaves the queue and never starts.
 * @param call the call to run, holding its place until its answer is fully read
 * @param signal optional abort signal of the call
 */
export const runLimited = <Result>(
  call: () => Promise<Result>,
  signal?: AbortSignal
): Promise<Result> =>
  new Promise<Result>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const start = () => {
      runningCalls += 1;
      call()
        .then(resolve, reject)
        .finally(() => {
          runningCalls -= 1;
          startWaitingCalls();
        });
    };
    const waiting: WaitingCall = {
      start,
      signal,
      onAbort: () => {
        const position = waitingCalls.indexOf(waiting);
        if (position >= 0) {
          waitingCalls.splice(position, 1);
        }
        reject(signal?.reason);
      },
    };
    signal?.addEventListener('abort', waiting.onAbort, { once: true });
    waitingCalls.push(waiting);
    startWaitingCalls();
  });
